import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Container,
  Grid,
  Header,
  Segment,
  Card,
  Icon,
  Statistic,
  Image,
  Button,
  Divider,
  Table,
  Label,
  Input,
  Dropdown,
  Modal,
  Message,
  List
} from 'semantic-ui-react';
import { AuthStdFooter } from "../../utils/auth-std-footer";
// import { getMenuIdForFeature } from '../../utils/roleUtils';
import useRoleNormalization from '../../hooks/useRoleNormalization';
import '../../styles/landing-pages.css';
import '../../styles/article.css';
import userPuzzle from '../../assets/userPuzzle.svg';
import voiceActressBW from '../../assets/micAndshield.svg';
import bookStack from '../../assets/justBookStack.svg';
import lightening from '../../assets/lightening.svg';
// import checkBox from '../../assets/checkListLine.svg';
import robotGearRocket from '../../assets/robotGearRocket.svg';
import warningIcon from '../../assets/info_icon_red.svg';
import activeUsersIcon from '../../assets/active-users.svg';

// Import RealUserService for fetching user data
import realUserService from '../../components/messaging/services/RealUserService';
// Import Database Audit component
import DatabaseAudit from './DatabaseAudit';
// Import ElevenLabs voice-slot audit/reclaim panel
import VoiceSlotManager from './VoiceSlotManager';

const UserManagement = () => {
  const userProfile = useSelector((state) => state.user.profile);
  const userName = userProfile?.data?.title || 'Administrator';
  const navigate = useNavigate();

  // Initialize role normalization hook
  const {
    normalizeRole,
    getRoleColor,
    getRoleIcon,
    getRoleLabel,
    getRoleFilterOptions
  } = useRoleNormalization();

  // State management
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDatabaseAudit, setShowDatabaseAudit] = useState(false);
  const [showVoiceSlots, setShowVoiceSlots] = useState(false);
  const [userStats, setUserStats] = useState({
    total: 0,
    voiceActors: 0,
    bookCreators: 0,
    admins: 0,
    active: 0,
    inactive: 0
  });

  const loadUsers = async () => {
    setLoading(true);
    setError(null);

    try {
      // Pass the current user from Redux to RealUserService
      const currentUser = userProfile?.data ? {
        userId: userProfile.data.userId || userProfile.data.id,
        ...userProfile.data
      } : null;

      console.log('UserManagement: Attempting to load users with current user:', currentUser?.userId);

      const userData = await realUserService.getAllRealData(currentUser);

      if (userData && userData.users) {
        const userList = Object.values(userData.users.all || userData.users);
        console.log('UserManagement: Successfully loaded users:', userList.length);
        setUsers(userList);
        calculateStats(userList);
      } else {
        throw new Error('No user data received');
      }
    } catch (err) {
      console.error('Failed to load users:', err);
      setError(`Failed to load users: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };


  const filterUsers = () => {
    let filtered = users;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.userId?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply role filter using normalized roles
    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => {
        const userRole = normalizeRole(user.primaryRole || user.role);
        return userRole === roleFilter;
      });
    }

    setFilteredUsers(filtered);
  };

  // Load users on component mount
  useEffect(() => {
    loadUsers();
  }, []);

  // Filter users when search term or role filter changes
  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, roleFilter]);

  const calculateStats = (userList) => {
    const stats = {
      total: userList.length,
      voiceActors: userList.filter(u => u.role === 'voiceActor' || u.primaryRole === 'voiceActor').length,
      bookCreators: userList.filter(u => u.role === 'bookCreator' || u.primaryRole === 'bookCreator').length,
      admins: userList.filter(u => u.role === 'admin' || u.primaryRole === 'admin').length,
      active: userList.filter(u => u.isActive !== false).length,
      inactive: userList.filter(u => u.isActive === false).length
    };
    setUserStats(stats);
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setShowUserModal(true);
  };

  // const handleDeleteUser = (user) => {
  //   setSelectedUser(user);
  //   setShowDeleteModal(true);
  // };

  const confirmDeleteUser = () => {
    // This will be implemented in Phase 2 with the full deletion protocol
    console.log('Delete user:', selectedUser);
    setShowDeleteModal(false);
    setSelectedUser(null);
    // TODO: Implement actual deletion logic
  };

  // Use getRoleLabel from hook instead of formatRole
  const formatRole = (role) => getRoleLabel(role) || 'Unknown';

  // Quick action cards for user management
  const quickActions = [
    {
      icon: 'plus',
      img: userPuzzle,
      title: 'Add New User',
      description: 'Create a new user account',
      action: () => navigate('/admin/add-user'),
      color: 'green'
    },
    {
      icon: 'database',
      img: lightening,
      title: 'Database Audit',
      description: 'Audit database health and find orphans',
      action: () => setShowDatabaseAudit(true),
      color: 'blue'
    },
    {
      icon: 'microphone',
      img: lightening,
      title: 'Voice Slots',
      description: 'ElevenLabs cap headroom, orphans & reclaim',
      action: () => setShowVoiceSlots(true),
      color: 'purple'
    },
    {
      icon: 'download',
      img: robotGearRocket,
      title: 'Export Users',
      description: 'Export user data to CSV',
      action: () => console.log('Export users'), // TODO: Implement
      color: 'teal'
    },
    {
      icon: 'trash',
      img: warningIcon,
      title: 'Delete User',
      description: 'Safely remove user accounts',
      action: () => navigate('/admin/delete-user'),
      color: 'red'
    }
  ];

  // Get role filter options from the hook
  const roleFilterOptions = getRoleFilterOptions(true);

  return (
    <Container fluid className="landing-page admin-landing">
      <Container style={{ marginTop: "30px" }}>
        <Grid className="articleListWidth">
          <Grid.Row>
            <Grid.Column width={3}>
              {/* <div className="img-with-gradient"> */}
              <div className="">

                <Image src={userPuzzle} style={{ width: '140px', height: '140px' }} />
              </div>
            </Grid.Column>
            <Grid.Column width={13} textAlign="center">
              <span className="blgListItemTitle">User Management</span>
              <span className="blgListSubTitle">Comprehensive User Administration</span>
              <span className="blgListAuthor">Welcome Admin - {userName}</span>

              <p className="landing-subheading-text">
                Manage all user accounts, roles, and permissions. View user statistics,
                search and filter users, and perform administrative actions.
              </p>
              <div style={{ textAlign: 'left', marginTop: '1em' }}>
                <Button
                  onClick={loadUsers}
                  size="large"
                  className="landing-goto-button"
                  loading={loading}
                  icon="refresh"
                  content="Refresh User Data"
                />
              </div>
            </Grid.Column>
          </Grid.Row>
        </Grid>
        <Grid className="articleListWidth" style={{ marginTop: "20px", borderTop: "solid 1px #AAAAAA" }}></Grid>
      </Container>

      <Container style={{ marginTop: '2em', marginBottom: '2em' }}>
        {/* Error Message */}
        {error && (
          <Message negative>
            <Message.Header>Error Loading Users</Message.Header>
            <p>{error}</p>
            <Button onClick={loadUsers} primary>Retry</Button>
          </Message>
        )}

        {/* User Statistics */}
        <Grid>
          <Grid.Row>
            <Grid.Column width={6}>
              <Header as="h2">
                <img src={robotGearRocket} alt="" style={{ width: '90px' }} />
                <Header.Content style={{
                  fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
                  fontWeight: '400',
                  fontSize: '2.5rem'
                }}>
                  User Statistics
                  <Header.Subheader>Current user metrics and distribution</Header.Subheader>
                </Header.Content>
              </Header>
            </Grid.Column>
            <Grid.Column width={10}>
              <Statistic.Group widths="five">
                <Statistic>
                  <Statistic.Value>
                    <div style={{
                      fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
                      fontWeight: '300',
                      fontSize: '3.0rem'
                    }}>
                      <img src={userPuzzle} alt="" style={{ width: '40px' }} /> {userStats.total}
                    </div>
                  </Statistic.Value>
                  <Statistic.Label>Total Users</Statistic.Label>
                </Statistic>

                <Statistic>
                  <Statistic.Value>
                    <div style={{
                      fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
                      fontWeight: '300',
                      fontSize: '3.0rem'
                    }}>
                      <img src={voiceActressBW} alt="" style={{ width: '40px' }} /> {userStats.voiceActors}
                    </div>
                  </Statistic.Value>
                  <Statistic.Label>Voice Actors</Statistic.Label>
                </Statistic>

                <Statistic>
                  <Statistic.Value>
                    <div style={{
                      fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
                      fontWeight: '300',
                      fontSize: '3.0rem'
                    }}>
                      <img src={bookStack} alt="" style={{ width: '40px' }} /> {userStats.bookCreators}
                    </div>
                  </Statistic.Value>
                  <Statistic.Label>Book Creators</Statistic.Label>
                </Statistic>

                <Statistic>
                  <Statistic.Value>
                    <div style={{
                      fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
                      fontWeight: '300',
                      fontSize: '3.0rem'
                    }}>
                      <img
                        src="/static/media/thodkin_librarian_futuristic_2a188bb5-2c71-4330-a561-ef70e9707b82.69a27af8cce99fd638a3.png"
                        alt="Admin Profile"
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          objectFit: 'cover',
                          marginRight: '8px'
                        }}
                      /> {userStats.admins}
                    </div>
                  </Statistic.Value>
                  <Statistic.Label>Administrators</Statistic.Label>
                </Statistic>

                <Statistic>
                  <Statistic.Value>
                    <div style={{
                      fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
                      fontWeight: '300',
                      fontSize: '3.0rem'
                    }}>
                      <img src={activeUsersIcon} alt="" style={{ width: '90px' }} />{userStats.active}
                    </div>
                  </Statistic.Value>
                  <Statistic.Label>Active Users</Statistic.Label>
                </Statistic>
              </Statistic.Group>
            </Grid.Column>
          </Grid.Row>
        </Grid>

        <Divider />

        {/* Quick Actions */}
        <Header as="h2" dividing style={{ marginTop: '2em' }}>
          <img src={lightening} alt="" />
          <Header.Content>
            Quick Actions
            <Header.Subheader>Common user management tasks</Header.Subheader>
          </Header.Content>
        </Header>

        <Card.Group itemsPerRow={4} stackable>
          {quickActions.map((action, index) => (
            <Card
              key={index}
              onClick={action.action}
              raised
              style={{ cursor: 'pointer' }}
            >
              <Card.Content>
                <img src={action.img} alt="" style={{ height: '80px', display: 'block', margin: '0 auto 1em', color: '#3d6a87' }} />
                <Card.Header textAlign="center">{action.title}</Card.Header>
                <Card.Description textAlign="center">
                  {action.description}
                </Card.Description>
              </Card.Content>
            </Card>
          ))}
        </Card.Group>

        {/* User Search and Filter */}
        <Header as="h2" dividing style={{ marginTop: '2em' }}>
          <Icon name="search" />
          <Header.Content>
            User Directory
            <Header.Subheader>Search and manage user accounts</Header.Subheader>
          </Header.Content>
        </Header>

        <Grid columns={2} stackable>
          <Grid.Column>
            <Input
              icon="search"
              placeholder="Search users by name, email, or ID..."
              value={searchTerm}
              onChange={(e, { value }) => setSearchTerm(value)}
              fluid
            />
          </Grid.Column>
          <Grid.Column>
            <Dropdown
              placeholder="Filter by role"
              fluid
              selection
              options={roleFilterOptions}
              value={roleFilter}
              onChange={(e, { value }) => setRoleFilter(value)}
            />
          </Grid.Column>
        </Grid>

        {/* User Table */}
        <Segment loading={loading} style={{ marginTop: '1em' }}>
          <Table celled striped>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>User</Table.HeaderCell>
                <Table.HeaderCell>Role</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell>Last Active</Table.HeaderCell>
                <Table.HeaderCell>Actions</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {filteredUsers.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan="5" textAlign="center">
                    {loading ? 'Loading users...' : 'No users found matching your criteria.'}
                  </Table.Cell>
                </Table.Row>
              ) : (
                filteredUsers.map((user, index) => (
                  <Table.Row key={user.userId || index}>
                    <Table.Cell>
                      <Header as="h4" image>
                        <Icon name={getRoleIcon(user.primaryRole || user.role)} />
                        <Header.Content>
                          {user.name || 'Unknown Name'}
                          <Header.Subheader>
                            {user.email || 'No email'}
                            <br />
                            <small>ID: {user.userId || 'No ID'}</small>
                          </Header.Subheader>
                        </Header.Content>
                      </Header>
                    </Table.Cell>
                    <Table.Cell>
                      <Label color={getRoleColor(user.primaryRole || user.role)}>
                        <Icon name={getRoleIcon(user.primaryRole || user.role)} />
                        {formatRole(user.primaryRole || user.role)}
                      </Label>
                    </Table.Cell>
                    <Table.Cell>
                      <Label color={user.isActive !== false ? 'green' : 'red'}>
                        <Icon name={user.isActive !== false ? 'check circle' : 'times circle'} />
                        {user.isActive !== false ? 'Active' : 'Inactive'}
                      </Label>
                    </Table.Cell>
                    <Table.Cell>
                      {user.lastLoginDate ? new Date(user.lastLoginDate).toLocaleDateString() : 'Never'}
                    </Table.Cell>
                    <Table.Cell>
                      <Button.Group size="small">
                        <Button
                          icon="eye"
                          onClick={() => handleUserSelect(user)}
                          title="View Details"
                        />
                        <Button
                          icon="edit"
                          onClick={() => navigate(`/admin/user-profile/${user.userId}`)}
                          title="Edit User"
                        />
                        {/* <Button
                          icon="trash"
                          color="red"
                          onClick={() => handleDeleteUser(user)}
                          title="Delete User"
                        /> */}
                      </Button.Group>
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table>
        </Segment>

        {/* Pagination would go here */}
        {filteredUsers.length > 0 && (
          <div style={{ textAlign: 'center', marginTop: '1em' }}>
            <Label>
              Showing {filteredUsers.length} of {users.length} users
            </Label>
          </div>
        )}
      </Container>

      {/* User Details Modal */}
      <Modal
        open={showUserModal}
        onClose={() => setShowUserModal(false)}
        size="small"
      >
        <Modal.Header>
          <Icon name="user" />
          User Details: {selectedUser?.name || 'Unknown'}
        </Modal.Header>
        <Modal.Content scrolling>
          {selectedUser && (
            <Grid columns={2} divided>
              <Grid.Column>
                <Header as="h3">Basic Information</Header>
                <List>
                  <List.Item>
                    <List.Icon name="user" />
                    <List.Content>
                      <List.Header>Name</List.Header>
                      {selectedUser.name || 'Not provided'}
                    </List.Content>
                  </List.Item>
                  <List.Item>
                    <List.Icon name="mail" />
                    <List.Content>
                      <List.Header>Email</List.Header>
                      {selectedUser.email || 'Not provided'}
                    </List.Content>
                  </List.Item>
                  <List.Item>
                    <List.Icon name="phone" />
                    <List.Content>
                      <List.Header>Phone</List.Header>
                      {selectedUser.phone || 'Not provided'}
                    </List.Content>
                  </List.Item>
                  <List.Item>
                    <List.Icon name="id badge" />
                    <List.Content>
                      <List.Header>User ID</List.Header>
                      {selectedUser.userId || 'Not provided'}
                    </List.Content>
                  </List.Item>
                  <List.Item>
                    <List.Icon name="shield" />
                    <List.Content>
                      <List.Header>Role</List.Header>
                      {formatRole(selectedUser.primaryRole || selectedUser.role)}
                    </List.Content>
                  </List.Item>
                </List>
              </Grid.Column>
              <Grid.Column>
                <Header as="h3">Account Status</Header>
                <List>
                  <List.Item>
                    <List.Icon name={selectedUser.isActive !== false ? 'check circle' : 'times circle'}
                      color={selectedUser.isActive !== false ? 'green' : 'red'} />
                    <List.Content>
                      <List.Header>Status</List.Header>
                      {selectedUser.isActive !== false ? 'Active' : 'Inactive'}
                    </List.Content>
                  </List.Item>
                  <List.Item>
                    <List.Icon name="calendar" />
                    <List.Content>
                      <List.Header>Created</List.Header>
                      {selectedUser.createdDate ? new Date(selectedUser.createdDate).toLocaleDateString() : 'Unknown'}
                    </List.Content>
                  </List.Item>
                  <List.Item>
                    <List.Icon name="clock" />
                    <List.Content>
                      <List.Header>Last Login</List.Header>
                      {selectedUser.lastLoginDate ? new Date(selectedUser.lastLoginDate).toLocaleDateString() : 'Never'}
                    </List.Content>
                  </List.Item>
                </List>
              </Grid.Column>
            </Grid>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowUserModal(false)}>Close</Button>
          <Button primary onClick={() => {
            setShowUserModal(false);
            navigate(`/admin/user-profile/${selectedUser.userId}`);
          }}>
            <Icon name="edit" />
            Edit User
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        size="small"
      >
        <Modal.Header>
          <Icon name="warning sign" color="red" />
          Confirm User Deletion
        </Modal.Header>
        <Modal.Content>
          <Message warning>
            <Message.Header>Warning: This action cannot be undone</Message.Header>
            <p>
              You are about to delete user <strong>{selectedUser?.name}</strong> ({selectedUser?.email}).
              This will remove all associated data including profiles, messages, and relationships.
            </p>
          </Message>
          <p>
            <strong>User Details:</strong>
          </p>
          <List>
            <List.Item>Name: {selectedUser?.name}</List.Item>
            <List.Item>Email: {selectedUser?.email}</List.Item>
            <List.Item>Role: {formatRole(selectedUser?.primaryRole || selectedUser?.role)}</List.Item>
            <List.Item>User ID: {selectedUser?.userId}</List.Item>
          </List>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowDeleteModal(false)}>Cancel</Button>
          <Button color="red" onClick={confirmDeleteUser}>
            <Icon name="trash" />
            Delete User
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Database Audit Modal */}
      {showDatabaseAudit && (
        <DatabaseAudit onClose={() => setShowDatabaseAudit(false)} />
      )}

      {/* ElevenLabs Voice Slots Modal */}
      {showVoiceSlots && (
        <VoiceSlotManager onClose={() => setShowVoiceSlots(false)} />
      )}

      {AuthStdFooter()}
    </Container>
  );
};

export default UserManagement;
